import { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import colors from '../constants/colors';
import Layout from '../components/Layout';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaUserGraduate,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    class_id: '',
    division: '',
  });
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const { selectedAcademicYear } = useAppContext();

  useEffect(() => {
    fetchClasses();
    fetchBatches();
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  const fetchClasses = async () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-classes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.status === 'success') {
        setClasses(response.data.data);
      } else {
        alert('Failed to fetch classes');
      }
    } catch (error) {
      console.error('Failed to fetch classes', error);
      alert('Error fetching classes');
    }
  };

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setAlert({
        show: true,
        message: 'Please select an academic year.',
        variant: 'warning',
      });
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      if (response.data.status === 'success') {
        setBatches(response.data.data);
      } else {
        setAlert({
          show: true,
          message: 'Failed to fetch batches',
          variant: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error fetching batches',
        variant: 'danger',
      });
      console.error('Failed to fetch batches', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setAlert({
        show: true,
        message: 'Please select an academic year.',
        variant: 'warning',
      });
      return;
    }

    try {
      const url = formData.id
        ? `${appConstants.API_ROOT_URL}update-batch`
        : `${appConstants.API_ROOT_URL}create-batch`;
      const payload = {
        ...formData,
        academic_year_id: selectedAcademicYear.id,
      };
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        fetchBatches();
        setShowModal(false);
        setAlert({
          show: true,
          message: 'Batch saved successfully!',
          variant: 'success',
        });
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to save batch',
          variant: 'danger',
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'An error occurred while saving the batch.';
      setAlert({ show: true, message: errorMessage, variant: 'danger' });
      console.error('Error saving batch:', error);
    }
  };

  const openModal = (batch = { id: null, class_id: '', division: '' }) => {
    // If editing, find the class_id using class_name
    const classData = classes.find((c) => c.name === batch.class_name);
    setFormData({
      id: batch.id || null,
      class_id: classData ? classData.id : '', // Assign correct class_id
      division: batch.division || '',
    });
    setShowModal(true);
  };

  // Filtered and searched batches
  const filteredBatches = batches.filter((batch) => {
    const matchesClass = classFilter
      ? (() => {
          // Find the selected class by ID
          const selectedClass = classes.find(
            (c) => c.id === parseInt(classFilter),
          );
          // Compare batch class_name with the selected class name
          return selectedClass && batch.class_name === selectedClass.name;
        })()
      : true;
    const matchesSearch = search
      ? batch.class_name.toLowerCase().includes(search.toLowerCase()) ||
        (batch.division &&
          batch.division.toLowerCase().includes(search.toLowerCase()))
      : true;
    return matchesClass && matchesSearch;
  });

  return (
    <Layout>
      {/* Compact Dark Top Bar - similar to StudentListV2 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '12px',
          backgroundColor: colors.sidebar.background,
          padding: '6px',
          borderRadius: '6px',
          color: colors.sidebar.text,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Back Button */}
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => window.history.back()}
            style={{
              borderRadius: '6px',
              width: '34px',
              height: '34px',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Back"
          >
            <FaChevronLeft />
          </Button>

          {/* Title */}
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
              marginRight: 16,
            }}
          >
            Batches
          </span>

          {/* Search Input */}
          <InputGroup style={{ width: '220px' }}>
            <InputGroup.Text
              style={{
                padding: '6px 8px',
                background: 'transparent',
                border: 'none',
                color: colors.sidebar.text,
              }}
            >
              <FaSearch style={{ color: colors.sidebar.text }} />
            </InputGroup.Text>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                height: '30px',
                backgroundColor: colors.card.background,
                borderColor: colors.card.border,
                color: colors.text.primary,
              }}
            />
          </InputGroup>

          {/* Class Filter */}
          <Form.Select
            size="sm"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            style={{
              width: '160px',
              backgroundColor: colors.card.background,
              borderColor: colors.card.border,
              color: colors.text.primary,
            }}
          >
            <option value="">All Classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </div>

        {/* Right side actions */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Add Batch Button */}
          <Button
            variant="light"
            size="sm"
            onClick={() => openModal()}
            style={{
              width: '34px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
            }}
            title="Add Batch"
          >
            <FaPlus />
          </Button>
        </div>
      </div>

      {/* Alert Messages */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ ...alert, show: false })}
          style={{
            borderRadius: colors.card.radius,
            marginBottom: '20px',
            border: `1px solid ${colors.card.border}`,
            boxShadow: `0 2px 8px ${colors.card.shadow}`,
            backgroundColor:
              alert.variant === 'success'
                ? colors.status.successLight
                : alert.variant === 'danger'
                  ? colors.status.errorLight
                  : alert.variant === 'warning'
                    ? colors.status.warningLight
                    : colors.status.infoLight,
          }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Batches Table Card */}
      <div
        style={{
          backgroundColor: colors.card.background,
          borderRadius: colors.card.radius,
          padding: '12px',
          boxShadow: `0 2px 8px ${colors.card.shadow}`,
          minHeight: '60vh',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spinner
              animation="border"
              style={{ color: colors.primary[600] }}
            />
            <p style={{ marginTop: '15px', color: colors.text.secondary }}>
              Loading batches...
            </p>
          </div>
        ) : filteredBatches.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 0',
              color: colors.text.secondary,
            }}
          >
            <FaUserGraduate
              style={{
                fontSize: '3rem',
                opacity: 0.3,
                marginBottom: '15px',
                color: colors.text.muted,
              }}
            />
            {batches.length === 0 ? (
              // No batches at all
              <>
                <p style={{ color: colors.text.secondary }}>No batches found</p>
                <Button
                  variant="primary"
                  onClick={() => openModal()}
                  style={{
                    marginTop: '10px',
                    borderRadius: colors.card.radius,
                    padding: '10px 24px',
                    fontWeight: '600',
                    backgroundColor: colors.button.primary,
                    borderColor: colors.button.primary,
                    border: 'none',
                  }}
                >
                  <FaPlus /> Add First Batch
                </Button>
              </>
            ) : (
              // Batches exist but none match the filter
              <>
                <p style={{ color: colors.text.secondary }}>
                  No batches match your current filter
                </p>
              </>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <style>{`.batch-table td, .batch-table th { vertical-align: middle; }`}</style>
            <Table
              hover
              responsive
              className="batch-table"
              style={{ marginBottom: '8px' }}
            >
              <thead
                style={{
                  backgroundColor: colors.utility.white,
                  borderBottom: `2px solid ${colors.card.border}`,
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: '12px 10px',
                      fontWeight: '700',
                      color: colors.text.primary,
                      width: '10%',
                      whiteSpace: 'nowrap',
                      fontSize: '0.9rem',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      backgroundColor: colors.utility.white,
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      padding: '12px 10px',
                      fontWeight: '700',
                      color: colors.text.primary,
                      width: '40%',
                      whiteSpace: 'nowrap',
                      fontSize: '0.9rem',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      backgroundColor: colors.utility.white,
                    }}
                  >
                    Class
                  </th>
                  <th
                    style={{
                      padding: '12px 10px',
                      fontWeight: '700',
                      color: colors.text.primary,
                      width: '35%',
                      whiteSpace: 'nowrap',
                      fontSize: '0.9rem',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      backgroundColor: colors.utility.white,
                    }}
                  >
                    Division
                  </th>
                  <th
                    style={{
                      padding: '12px 10px',
                      fontWeight: '700',
                      color: colors.text.primary,
                      width: '15%',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      fontSize: '0.9rem',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      backgroundColor: colors.utility.white,
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBatches.map((batch, index) => (
                  <tr
                    key={batch.id}
                    style={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.table.rowBorder}`,
                    }}
                  >
                    <td
                      style={{
                        padding: '6px 10px',
                        fontSize: '0.9rem',
                        color: colors.text.secondary,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      style={{
                        padding: '6px 10px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: colors.text.primary,
                      }}
                    >
                      {batch.class_name}
                    </td>
                    <td
                      style={{
                        padding: '6px 10px',
                        fontSize: '0.9rem',
                        color: colors.text.primary,
                      }}
                    >
                      {batch.division}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openModal(batch)}
                        style={{
                          borderRadius: '6px',
                          padding: '4px 8px',
                          fontWeight: '600',
                          fontSize: '0.75rem',
                          background: colors.button.primary,
                          border: 'none',
                        }}
                        title="Edit Batch"
                      >
                        <FaEdit style={{ fontSize: '0.8rem' }} /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Batch */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        contentClassName="border-0"
        style={{
          '--bs-modal-bg': colors.card.background,
          '--bs-modal-border-color': colors.card.border,
        }}
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: colors.card.background,
            borderBottom: `1px solid ${colors.card.border}`,
          }}
        >
          <Modal.Title style={{ color: colors.text.primary }}>
            {formData.id ? 'Edit Batch' : 'Add Batch'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: colors.card.background }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label
                style={{ color: colors.form.label, fontWeight: '500' }}
              >
                Class
              </Form.Label>
              <Form.Select
                value={formData.class_id}
                onChange={(e) =>
                  setFormData({ ...formData, class_id: e.target.value })
                }
                style={{
                  backgroundColor: colors.form.background,
                  borderColor: colors.form.border,
                  color: colors.text.primary,
                }}
              >
                <option value="">Select Class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label
                style={{ color: colors.form.label, fontWeight: '500' }}
              >
                Division
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.division}
                onChange={(e) =>
                  setFormData({ ...formData, division: e.target.value })
                }
                style={{
                  backgroundColor: colors.form.background,
                  borderColor: colors.form.border,
                  color: colors.text.primary,
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: colors.card.background,
            borderTop: `1px solid ${colors.card.border}`,
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            style={{
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{
              backgroundColor: colors.button.primary,
              borderColor: colors.button.primary,
              color: '#ffffff',
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default BatchManagement;
